import { useEffect, useId, useRef, useState } from 'react'
import { LOGIN_ROUTE, POST_UNLOCK_ROUTE } from '@constants/routes'
import { PinPad, type PinPadHandle } from '@features/auth/components/PinPad'
import { UnlockPinSetupModal } from '@features/auth/components/UnlockPinSetupModal'
import { useUnlockForm } from '@features/auth/hooks/useUnlockForm'
import {
  getPinStatus,
  PinUnlockError,
  unlockWithPin,
  type PinStatus,
} from '@features/auth/services/pinVaultService'
import { sessionStore } from '@features/auth/services/sessionStore'
import { PasswordInput } from '@shared/ui/PasswordInput'
import { zeroize } from '@/crypto/zeroize'
import { useToast } from '@shared/ui/useToast'
import { useTranslation } from '@/i18n'

/**
 * #lock 画面 (T-C3) — マスターパスワード or PIN で unlock する。
 *
 * 設計判断:
 *   - PIN vault が設定済 (`pinVaultService.getStatus().configured`) なら、起動時の
 *     既定モードを `pin` にする (ookey-browser parity)。未設定なら従来通り password。
 *   - 「マスターパスワードで解錠」/「PIN で解錠」のトグルリンクで mode を切り替える。
 *   - PIN は N-box keypad (N = 設定時の桁数) で入力。全 box 埋まったら自動 submit。
 *   - PIN 失敗時は inline error + 残り試行回数表示。lockout で vault が消えたら
 *     password モードに強制 fallback (PinUnlockError reason='locked-out')。
 *   - master password 経路は既存の `useUnlockForm` をそのまま利用 (state machine 維持)。
 */

const LOCK_TITLE_ID = 'lock-title'

const LockGlyph = (): JSX.Element => (
  <svg
    width="38"
    height="38"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    aria-hidden="true"
  >
    <rect x={4.5} y={11} width={15} height={10} rx={2.5} />
    <path d="M8 11V7.5a4 4 0 0 1 8 0V11" />
    <circle cx={12} cy={16} r={1.3} fill="currentColor" stroke="none" />
  </svg>
)

type Mode = 'password' | 'pin'

export const LockPage = (): JSX.Element | null => {
  const { t } = useTranslation()
  const { state, isSubmitting, onSubmit, onSwitchAccount } = useUnlockForm()
  const toast = useToast()
  const passwordId = useId()

  const [pinStatus, setPinStatus] = useState<PinStatus | null>(null)
  const [mode, setMode] = useState<Mode>('password')
  const [pinValue, setPinValue] = useState('')
  const [pinBusy, setPinBusy] = useState(false)
  const [pinSetupOpen, setPinSetupOpen] = useState(false)

  const [pinError, setPinError] = useState<{
    messageKey: string
    attemptsLeft?: number
  } | null>(null)

  const pinPadRef = useRef<PinPadHandle>(null)
  const errorToastIdsRef = useRef<string[]>([])

  const material = sessionStore.getMaterial()
  const lockedEmail = material?.email ?? ''
  const isUnauth = sessionStore.getState().kind === 'unauth'

  // 起動時に PIN status を取得し、設定済なら既定モードを 'pin' に。
  useEffect(() => {
    let cancelled = false
    void (async () => {
      const s = await getPinStatus()
      if (cancelled) return
      setPinStatus(s)
      if (s.configured) setMode('pin')
    })()

    return () => {
      cancelled = true
    }
  }, [])

  // R-36: UNAUTH で #lock に到達したら #login へ。
  useEffect(() => {
    if (isUnauth) {
      window.location.hash = LOGIN_ROUTE
    }
  }, [isUnauth])

  // master password 失敗時に Toast を出す (既存挙動を維持)。
  useEffect(() => {
    if (state.kind === 'error' || state.kind === 'logged_out') {
      errorToastIdsRef.current.push(
        toast.show({ tone: 'danger', titleKey: state.messageKey, dedupe: true }),
      )
    }
    if (state.kind === 'unlocked') {
      errorToastIdsRef.current.forEach(id => toast.dismiss(id))
      errorToastIdsRef.current = []
    }
  }, [state, toast])

  if (isUnauth) return null

  const onPinComplete = async (pin: string): Promise<void> => {
    setPinError(null)
    setPinBusy(true)
    let sk: Uint8Array | null = null

    try {
      sk = await unlockWithPin(pin)
      // sessionStore.unlock は UNAUTH の場合 throw する (sessionStore.ts:146)。
      // PBKDF2 await 中に並列 refresh fail で UNAUTH 転落すると sk leak になるため、
      // 失敗時は zeroize して呼出側 catch に再送出する (review I-7)。
      try {
        sessionStore.unlock(sk)
        sk = null // sessionStore に所有権移譲済 — finally で zeroize しないように
      } catch (unlockErr) {
        zeroize(sk)
        sk = null
        throw unlockErr
      }
      window.location.hash = POST_UNLOCK_ROUTE
    } catch (err) {
      if (err instanceof PinUnlockError) {
        if (err.reason === 'locked-out') {
          // PIN vault が破棄され mode が password に切替わるため、PIN フォーム内の
          // inline error は renderPinForm が null を返して表示されない。
          // toast で必ず告知する (review I-3)。
          toast.show({
            tone: 'danger',
            titleKey: 'auth.lock.pin_locked_out',
            dedupe: true,
          })
          setPinError(null)
          setPinStatus({ configured: false })
          setMode('password')
        } else if (err.reason === 'wrong-pin') {
          setPinError({ messageKey: 'auth.lock.pin_wrong', attemptsLeft: err.attemptsLeft })
          pinPadRef.current?.clear()
        } else if (err.reason === 'no-session') {
          // sessionStore が UNAUTH に転落 — トップ effect で #login へ redirect
          // されるが、間に合わない場合に備えてここでも明示的に飛ばす。
          window.location.hash = LOGIN_ROUTE
        } else {
          setPinError({ messageKey: 'auth.lock.pin_failed' })
          pinPadRef.current?.clear()
        }
      } else {
        // sessionStore.unlock throw 等の想定外エラー: pin_failed を表示
        setPinError({ messageKey: 'auth.lock.pin_failed' })
        pinPadRef.current?.clear()
      }
    } finally {
      // 上の try で sessionStore に渡し損ねた sk があれば最終 zeroize (defense in depth)
      if (sk !== null) {
        zeroize(sk)
      }
      setPinBusy(false)
    }
  }

  const switchMode = (next: Mode): void => {
    setPinError(null)
    setPinValue('')
    pinPadRef.current?.clear()
    setMode(next)
  }

  const renderPinForm = (): JSX.Element | null => {
    if (pinStatus === null || !pinStatus.configured) return null

    return (
      <div className="lock-form">
        <PinPad
          ref={pinPadRef}
          length={pinStatus.pinLength}
          onChange={setPinValue}
          onComplete={pin => void onPinComplete(pin)}
          disabled={pinBusy}
          hasError={pinError !== null}
          ariaLabel={t('auth.lock.pin_aria_label')}
        />

        {pinError !== null && (
          <p className="form-error" role="alert">
            {t(pinError.messageKey)}
            {pinError.attemptsLeft !== undefined && (
              <span className="form-error-attempts">
                {t('auth.lock.pin_attempts_left', {
                  n: String(pinError.attemptsLeft),
                })}
              </span>
            )}
          </p>
        )}

        <button
          type="button"
          className="btn btn-primary btn-lg btn-block"
          disabled={pinBusy || pinValue.length !== pinStatus.pinLength}
          onClick={() => void onPinComplete(pinValue)}
        >
          {pinBusy ? t('auth.lock.submitting') : t('auth.lock.pin_submit')}
        </button>
      </div>
    )
  }

  const renderPasswordForm = (): JSX.Element => (
    <form className="lock-form" onSubmit={onSubmit} noValidate>
      <div className="field">
        <label className="field-label" htmlFor={passwordId}>
          {t('auth.field_password')}
        </label>
        <PasswordInput
          id={passwordId}
          name="password"
          size="lg"
          autoComplete="current-password"
          autoFocus
          required
          disabled={isSubmitting}
        />
      </div>

      {state.kind === 'error' && (
        <p className="form-error" role="alert">
          {t(state.messageKey)}{' '}
          <span className="form-error-attempts">
            {t('auth.lock.remaining_attempts', { count: state.remainingAttempts })}
          </span>
        </p>
      )}

      <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={isSubmitting}>
        {isSubmitting ? t('auth.lock.submitting') : t('auth.lock.submit')}
      </button>

      {/* PIN 未設定なら hint で onboarding 動線を出す (ookey-browser parity)。
          link をクリックすると UnlockPinSetupModal が開き、master password + PIN を
          1 ステップで設定できる。 */}
      {pinStatus !== null && !pinStatus.configured && (
        <p className="lock-pin-hint">
          {t('auth.lock.pin_setup_hint_prefix')}
          <button
            type="button"
            className="lock-pin-hint-link"
            onClick={() => setPinSetupOpen(true)}
          >
            {t('auth.lock.pin_setup_hint_action')}
          </button>
          {t('auth.lock.pin_setup_hint_suffix')}
        </p>
      )}
    </form>
  )

  const showPinForm = mode === 'pin' && pinStatus?.configured === true

  return (
    <div className="lock-screen">
      <span className="lock-bg" aria-hidden="true" />

      <div className="lock-center">
        <div className="lock-hero" aria-hidden="true">
          <span className="lock-hero-circle">
            <LockGlyph />
          </span>
        </div>

        <div className="lock-hero-text">
          <div className="lock-hero-title" id={LOCK_TITLE_ID}>
            {t('auth.lock.title')}
          </div>
          {lockedEmail !== '' && <div className="lock-hero-email">{lockedEmail}</div>}
          <div className="lock-hero-sub">
            {showPinForm ? t('auth.lock.pin_subtitle') : t('auth.lock.subtitle')}
          </div>
        </div>

        {showPinForm ? renderPinForm() : renderPasswordForm()}

        {/* submit ボタンのすぐ下に置いて、特に mobile で「別アカウント切替」動線を見つけやすくする。
            (旧版は footer 下端に置いていたため scroll が必要だった) */}
        <a
          href="#login"
          className="lock-link lock-switch-account"
          onClick={ev => {
            ev.preventDefault()
            onSwitchAccount()
          }}
        >
          {t('auth.lock.switch_account')}
        </a>

        {pinStatus?.configured === true && (
          <button
            type="button"
            className="lock-link lock-mode-toggle"
            onClick={() => switchMode(mode === 'pin' ? 'password' : 'pin')}
          >
            {mode === 'pin' ? t('auth.lock.use_master_password') : t('auth.lock.use_pin')}
          </button>
        )}
      </div>

      {pinSetupOpen && <UnlockPinSetupModal onClose={() => setPinSetupOpen(false)} />}
    </div>
  )
}
